// Copyright 2025, University of Maryland, All Rights Reserved
interface PedagogicalApproachData {
  teaching_methods?: string[];
  active_learning_strategies?: string[];
  learning_modalities?: {
    visual?: string;
    auditory?: string;
    kinesthetic?: string;
  };
  technology_integration?: string;
}

interface PedagogicalApproachProps {
  data: PedagogicalApproachData;
}

export function PedagogicalApproach({ data }: PedagogicalApproachProps) {
  if (!data) {
    return null;
  }

  const hasContent = 
    (data.teaching_methods && data.teaching_methods.length > 0) ||
    (data.active_learning_strategies && data.active_learning_strategies.length > 0) ||
    data.learning_modalities ||
    data.technology_integration;

  if (!hasContent) {
    return null;
  }

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg pdf-page-break-avoid">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <span className="text-3xl mr-3">👩‍🏫</span>
        Teaching & Learning Approach
      </h2>

      <div className="space-y-6">
        {/* Teaching Methods */}
        {data.teaching_methods && data.teaching_methods.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Teaching Methods</h3>
            <div className="flex flex-wrap gap-2">
              {data.teaching_methods.map((method, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium border border-gray-200"
                >
                  {method}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Active Learning Strategies */}
        {data.active_learning_strategies && data.active_learning_strategies.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Learning Strategies</h3>
            <div className="flex flex-wrap gap-2">
              {data.active_learning_strategies.map((strategy, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium border border-gray-200"
                >
                  {strategy}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Learning Modalities */}
        {data.learning_modalities && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Learning Modalities</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.learning_modalities.visual && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">👁️</span>
                    <h4 className="font-semibold text-gray-800">Visual</h4>
                  </div>
                  <p className="text-sm text-gray-600">{data.learning_modalities.visual}</p>
                </div>
              )}
              {data.learning_modalities.auditory && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">👂</span>
                    <h4 className="font-semibold text-gray-800">Auditory</h4>
                  </div>
                  <p className="text-sm text-gray-600">{data.learning_modalities.auditory}</p>
                </div>
              )}
              {data.learning_modalities.kinesthetic && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">✋</span>
                    <h4 className="font-semibold text-gray-800">Kinesthetic</h4>
                  </div>
                  <p className="text-sm text-gray-600">{data.learning_modalities.kinesthetic}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Technology Integration */}
        {data.technology_integration && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Technology Integration</h3>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-gray-700">{data.technology_integration}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
